import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../Footer";
import axios from "axios";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BtnLoader } from "../components/PageLoader";

const ForgetPassword = () => {
  const navigate = useNavigate();
  const optionToast = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    transition: Bounce,
  };
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [processing, setProcessing] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0); // Countdown in seconds
  const [canResendOtp, setCanResendOtp] = useState(false);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (otpCountdown > 0) {
      timer = setInterval(() => {
        setOtpCountdown((prev) => prev - 1);
      }, 1000);
    } else if (otpCountdown === 0 && showOtpInput) {
      setCanResendOtp(true);
    }
    return () => clearInterval(timer);
  }, [otpCountdown, showOtpInput]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordVisibilityConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const sendOTP = () => {
    setProcessing(true);
    if (!email) {
      toast.error("Email is required", optionToast);
      setProcessing(false);
      return;
    }

    if (!showOtpInput) {
      sendOtpData();
    } else {
      verifyOtp();
    }
  };

  const sendOtpData = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/auth/send-email-otp",
        {
          email: email,
          purpose: "forgot_password",
        }
      );

      if (response?.data?.success) {
        toast.success(response?.data?.message || "OTP sent successfully", optionToast);
        setShowOtpInput(true);
        setOtpCountdown(300); // Reset to 5 minutes
        setCanResendOtp(false);
      } else {
        toast.error(response?.data?.message, optionToast);
      }
    } catch (error) {
      console.log("An error occurred while fetching data.", error);
      toast.error("Failed to send OTP. Please try again.", optionToast);
    }
    setProcessing(false);
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    
    setProcessing(true);
    try {
      await sendOtpData();
    } catch (error) {
      console.log("An error occurred while fetching data.", error);
      toast.error("Failed to resend OTP. Please try again.", optionToast);
    }
    setProcessing(false);
  };

  const verifyOtp = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(window.CARLOCA_API + "/auth/verify-email-otp", {
        email,
        otp,
        purpose: "forgot_password",
      });

      if (response?.data?.success) {
        setShowPasswordInput(true);
        setShowOtpInput(false);
        toast.success("OTP Verified", optionToast);
      } else {
        toast.error(response?.data?.message || "Invalid OTP", optionToast);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Invalid OTP", optionToast);
    }
    setProcessing(false);
  };

  const updatePassword = () => {
    setProcessing(true);
    if (!password || !confirmPassword) {
      toast.error("Both password fields are required", optionToast);
      setProcessing(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Password does not match the confirmation password", optionToast);
      setProcessing(false);
      return;
    }

    saveData();
  };

  const saveData = async () => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/auth/reset-password",
        {
          email: email,
          password: password,
          password_confirmation: confirmPassword,
        }
      );

      if (response?.data?.success) {
        toast.success(response?.data?.message, optionToast);
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setOtp("");
        setShowOtpInput(false);
        setShowPasswordInput(false);
        setOtpCountdown(0);
        setCanResendOtp(false);
        navigate('/login');
      } else {
        toast.error(response?.data?.message, optionToast);
      }
    } catch (error) {
      console.log("An error occurred while fetching data.", error);
      toast.error("Password update failed. Please try again.", optionToast);
    }
    setProcessing(false);
  };

  return (
    <>
      <Navbar />
      <div className="cl-auth-page" style={{ paddingTop: 78 }}>
        <div className="cl-auth-card" style={{ maxWidth: 460 }}>
          <div className="text-center mb-3">
            <img src="/img/Logo_trusted_Cars.png" alt="Trusted Cars" style={{ height: 44, marginBottom: 10 }} />
          </div>
          <h5 className="cl-auth-title">Reset Password</h5>
          <p className="cl-auth-subtitle">Enter your email to receive a verification code</p>

          <form onSubmit={(e) => e.preventDefault()}>
            <div className="mb-3">
              <label className="cl-form-label">Email Address</label>
              <div className="position-relative">
                <input type="email" placeholder="you@email.com" className="cl-form-control" style={{ paddingLeft: 38 }}
                  onChange={(e) => setEmail(e.target.value)} value={email}
                  disabled={showOtpInput || showPasswordInput} />
                <i className="fa-solid fa-envelope" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b0bac5', fontSize: 14 }}></i>
              </div>
            </div>

            {showOtpInput && (
              <div className="mb-3">
                <label className="cl-form-label">OTP Code</label>
                <div className="position-relative">
                  <input type="text" placeholder="Enter OTP" className="cl-form-control" style={{ paddingLeft: 38 }}
                    value={otp} onChange={(e) => setOtp(e.target.value)} />
                  <i className="fa-solid fa-key" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b0bac5', fontSize: 14 }}></i>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  {otpCountdown > 0 ? (
                    <small className="text-muted">Resend in {formatTime(otpCountdown)}</small>
                  ) : (
                    <small className="text-primary" style={{ cursor: 'pointer' }} onClick={handleResendOtp}>Resend OTP</small>
                  )}
                </div>
              </div>
            )}

            {showPasswordInput && (
              <>
                <div className="mb-3">
                  <label className="cl-form-label">New Password</label>
                  <div className="position-relative">
                    <input type={showPassword ? 'text' : 'password'} placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" className="cl-form-control" style={{ paddingLeft: 38, paddingRight: 40 }}
                      value={password} onChange={(e) => setPassword(e.target.value)} />
                    <i className="fa-solid fa-lock" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b0bac5', fontSize: 14 }}></i>
                    <div style={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)', cursor: 'pointer' }} onClick={togglePasswordVisibility}>
                      <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ color: '#b0bac5', fontSize: 14 }}></i>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="cl-form-label">Confirm Password</label>
                  <div className="position-relative">
                    <input type={showPasswordConfirm ? 'text' : 'password'} placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" className="cl-form-control" style={{ paddingLeft: 38, paddingRight: 40 }}
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                    <i className="fa-solid fa-lock" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b0bac5', fontSize: 14 }}></i>
                    <div style={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)', cursor: 'pointer' }} onClick={togglePasswordVisibilityConfirm}>
                      <i className={`fa-solid ${showPasswordConfirm ? 'fa-eye-slash' : 'fa-eye'}`} style={{ color: '#b0bac5', fontSize: 14 }}></i>
                    </div>
                  </div>
                </div>
              </>
            )}

            {showPasswordInput ? (
              <button type="button" onClick={updatePassword} disabled={processing} className="cl-btn cl-btn-primary cl-btn-lg w-100">
                {processing ? <><BtnLoader />Updating...</> : 'Update Password'}
              </button>
            ) : (
              <button type="button" onClick={sendOTP} disabled={processing || !email} className="cl-btn cl-btn-primary cl-btn-lg w-100">
                {processing ? <><BtnLoader />Sending...</> : showOtpInput ? 'Verify OTP' : 'Send OTP'}
              </button>
            )}

            <div className="cl-auth-divider mt-3">or</div>
            <Link to="/login" className="cl-btn cl-btn-outline cl-btn-lg w-100">Back to Sign In</Link>
          </form>
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default ForgetPassword;